import React from 'react'
import { X, Loader2, CheckCircle2, AlertCircle } from 'lucide-react'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Textarea } from 'components/ui/textarea'
import DocumentAvatar from './DocumentAvatar'

export type UploadRowStatus =
	| 'pending'
	| 'uploading'
	| 'success'
	| 'error'

export interface UploadFileRowData {
	id: string
	file: File
	libraryName: string
	changeNote: string
	status: UploadRowStatus
	errorMessage?: string
}

interface UploadFileRowProps {
	row: UploadFileRowData
	progress: number | undefined
	matchesExisting: boolean
	libraryNameInvalid: boolean
	fileTooLarge: boolean
	disabled: boolean
	onChangeLibraryName: (value: string) => void
	onChangeNote: (value: string) => void
	onRemove: () => void
}

const formatBytes = (n: number): string => {
	if (n < 1024) return `${n} B`
	if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
	if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`
	return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`
}

const StatusIcon: React.FC<{ status: UploadRowStatus }> = ({ status }) => {
	if (status === 'uploading') {
		return <Loader2 className="w-4 h-4 text-[#0080fe] animate-spin" />
	}
	if (status === 'success') {
		return <CheckCircle2 className="w-4 h-4 text-green-600" />
	}
	if (status === 'error') {
		return <AlertCircle className="w-4 h-4 text-red-600" />
	}
	return null
}

const UploadFileRow: React.FC<UploadFileRowProps> = ({
	row,
	progress,
	matchesExisting,
	libraryNameInvalid,
	fileTooLarge,
	disabled,
	onChangeLibraryName,
	onChangeNote,
	onRemove,
}) => {
	const showChangeNote = matchesExisting
	const changeNoteRequired = matchesExisting
	const changeNoteInvalid =
		changeNoteRequired && !row.changeNote.trim() && row.status !== 'success'

	return (
		<div
			className={[
				'rounded-lg border p-3 flex flex-col gap-2',
				row.status === 'error'
					? 'border-red-300 bg-red-50/40'
					: 'border-gray-200 bg-white',
			].join(' ')}
		>
			<div className="flex items-start gap-3">
				<DocumentAvatar filename={row.file.name} size={36} />

				<div className="flex-1 min-w-0">
					<div className="text-sm text-gray-900 truncate" title={row.file.name}>
						{row.file.name}
					</div>
					<div className="text-xs text-gray-500">
						{formatBytes(row.file.size)}
						{matchesExisting && (
							<span className="ml-2 text-amber-600 font-medium">
								Existing document — uploads as a new version
							</span>
						)}
					</div>
					{fileTooLarge && (
						<div className="text-xs text-red-600 mt-1">
							File exceeds the upload size limit.
						</div>
					)}
					{row.status === 'error' && row.errorMessage && (
						<div className="text-xs text-red-600 mt-1">{row.errorMessage}</div>
					)}
				</div>

				<div className="flex items-center gap-2">
					<StatusIcon status={row.status} />
					<button
						type="button"
						onClick={onRemove}
						disabled={disabled || row.status === 'uploading'}
						className="text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
						aria-label="Remove from upload list"
					>
						<X className="w-4 h-4" />
					</button>
				</div>
			</div>

			<div className="flex flex-col gap-1">
				<Label
					htmlFor={`libraryName-${row.id}`}
					className="text-xs text-gray-700"
				>
					Library Name
				</Label>
				<Input
					id={`libraryName-${row.id}`}
					value={row.libraryName}
					onChange={e => onChangeLibraryName(e.target.value)}
					disabled={disabled || row.status === 'uploading' || row.status === 'success'}
					className={libraryNameInvalid ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : ''}
				/>
			</div>

			{showChangeNote && (
				<div className="flex flex-col gap-1">
					<Label
						htmlFor={`changeNote-${row.id}`}
						className={[
							'text-xs',
							changeNoteInvalid ? 'text-red-600' : 'text-gray-700',
						].join(' ')}
					>
						Change Note <span className="font-semibold">(required)</span>
					</Label>
					<Textarea
						id={`changeNote-${row.id}`}
						value={row.changeNote}
						onChange={e => onChangeNote(e.target.value)}
						placeholder="What changed in this version?"
						rows={2}
						disabled={disabled || row.status === 'uploading' || row.status === 'success'}
						className={changeNoteInvalid ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : ''}
					/>
				</div>
			)}

			{(row.status === 'uploading' || row.status === 'success') && (
				<div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
					<div
						className={[
							'h-full transition-all',
							row.status === 'success' ? 'bg-green-600' : 'bg-[#0080fe]',
						].join(' ')}
						style={{ width: `${row.status === 'success' ? 100 : (progress ?? 0)}%` }}
					/>
				</div>
			)}
		</div>
	)
}

export default UploadFileRow
